/**
 * @fileoverview Table with tabs for details pages
 * This component handles tabbed content rendering with support for tab scrolling and fade effects
 * @see {/src/hooks/useBreakPoints.ts}
 * @see {/src/store/content/content.selectors.ts}
 * @see {/src/components/ui/box/MainWrapper.tsx}
 * @active
 */

"use client";

import { Fragment, useEffect, useRef, useState, ReactElement, FC } from "react";

import {
  Box,
  Flex,
  Tab,
  // TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { useSelector } from "react-redux";

import { useActions } from "@/hooks/useActions";
import { useBreakPoints } from "@/hooks/useBreakPoints";
import contentSelectors from "@/store/content/content.selectors";
import { h_700_14_120_28, h_700_21_normal_42 } from "@/styles/fontStyles";

import { MainWrapper } from "../box/MainWrapper";

/**
 * Props interface for the TableInDetails component
 * @interface TableInDetailsProps
 * @active
 */
interface TableInDetailsProps {
  /** Data object to pass to tab panels */
  dataObject: Record<string, any>;

  /** Array of strings representing tab headers */
  tabsHeader: string[];

  /** Array of component functions to be rendered as tab panels */
  tabsPanels: FC<any>[];
}

/**
 * TableInDetails component for rendering tabbed content
 *
 * @param {TableInDetailsProps} props - Component props
 * @returns {ReactElement} The rendered component
 * @active
 */
export const TableInDetails: FC<TableInDetailsProps> = ({
  dataObject,
  tabsHeader,
  tabsPanels,
}): ReactElement => {
  const pathName = usePathname();
  const { isMobile } = useBreakPoints();
  const checker = () => {
    return pathName?.includes("people");
  };
  const indexTab = useSelector(contentSelectors.getCompanyDetailsTabIndex());

  const refTabHeader = useRef<HTMLDivElement | null>(null);
  const { setCompanyDetailsTabIndex } = useActions();

  const handleTabClick = (index: number) => {
    setCompanyDetailsTabIndex(index);
    if (index === 0 && refTabHeader.current) {
      refTabHeader.current.scrollLeft = 0;
    } else if (index === 3 && refTabHeader.current) {
      refTabHeader.current.scrollLeft = refTabHeader.current.scrollWidth;
    }
  };

  const [leftFade, setLeftFade] = useState<boolean>(false);
  const [rightFade, setRightFade] = useState<boolean>(false);

  useEffect(() => {
    const checkTabsVisibility = () => {
      if (!refTabHeader.current) return;

      const tabsContainer = refTabHeader.current;
      // Apply proper types to DOM elements
      const firstTab = tabsContainer.children[0] as HTMLElement;
      const lastTab = tabsContainer.children[
        tabsContainer.children.length - 1
      ] as HTMLElement;

      const isPartiallyVisibleFirst =
        firstTab && firstTab.offsetLeft < tabsContainer.scrollLeft;
      const isPartiallyVisibleLast =
        lastTab &&
        lastTab.offsetLeft + lastTab.offsetWidth >
          tabsContainer.offsetWidth + tabsContainer.scrollLeft;

      if (isPartiallyVisibleFirst && isPartiallyVisibleLast) {
        setLeftFade(true);
        setRightFade(true);
      } else if (isPartiallyVisibleFirst) {
        setLeftFade(true);
        setRightFade(false);
      } else if (isPartiallyVisibleLast) {
        setLeftFade(false);
        setRightFade(true);
      } else {
        setLeftFade(false);
        setRightFade(false);
      }
    };
    if (!refTabHeader.current) return;

    const temp = refTabHeader.current;
    window.addEventListener("resize", checkTabsVisibility);
    temp.addEventListener("scroll", checkTabsVisibility);
    checkTabsVisibility();

    return () => {
      if (temp) {
        temp.removeEventListener("scroll", checkTabsVisibility);
      }
      window?.removeEventListener("resize", checkTabsVisibility);
    };
  }, []);

  return (
    <Tabs
      isLazy={false}
      borderBottom={"2px solid transparent"}
      w={"full"}
      onChange={(index) => handleTabClick(index)}
    >
      <MainWrapper
        customStyleOuter={{ px: { base: "24px", md: "40px", xl: "0px" } }}
        customStyleInner={{
          maxW: {
            base: "100%",
            md: "100%",
            xl: "1124px",
          },
          position: "relative",
        }}
      >
        {leftFade && (
          <Box
            w={"46px"}
            h={"24px"}
            position={"absolute"}
            top={"3px"}
            left={"0"}
            bgGradient={
              "linear-gradient(90deg, #F0F2F5 0%, rgba(240, 242, 245, 0.00) 77.17%)"
            }
            zIndex={10000}
            cursor={"pointer"}
            onClick={(e) => {
              e.stopPropagation();
              handleTabClick(0);
            }}
          ></Box>
        )}

        <TabList
          alignItems={"flex-end"}
          position={"relative"}
          mb={{ base: "0px", md: "20px" }}
          borderBottom={"unset"}
          overflowX={"auto"}
          w={{
            base: "100%",
            // base: 'fit-content',
            md: "fit-content",
          }}
          sx={{
            "::-webkit-scrollbar": {
              width: "12px",
            },
          }}
          ref={refTabHeader}
          // pathName?.includes('people') ? '449px' :
        >
          <Flex
            position={"relative"}
            alignItems={"flex-end"}
            gap={"20px"}
            minW={{ base: "fit-content", md: "fit-content" }}
            justifyContent={{ base: "left", sm: "left" }}
            w={{ base: "100%", sm: "fit-content" }}
          >
            {tabsHeader?.length > 0 &&
              tabsHeader?.map((tab, index) => (
                <Fragment key={`${tab}-${index}`}>
                  <Tab
                    {...(isMobile ? h_700_14_120_28 : h_700_21_normal_42)}
                    marginBottom={"0px"}
                    borderBottom={"unset"}
                    px={"0px"}
                    whiteSpace={"nowrap"}
                    flexDir={"column"}
                    position={"relative"}
                    onClick={() => {
                      handleTabClick(index);
                    }}
                  >
                    <Text as={"h3"}>{tab}</Text>
                    <Box
                      position={"absolute"}
                      bottom={"0px"}
                      w={"full"}
                      h={"5px"}
                      borderRadius={"4px"}
                      bgColor={index === indexTab ? "red" : "transparent"}
                      zIndex={"500"}
                      onClick={() => {}}
                    ></Box>
                  </Tab>
                </Fragment>
              ))}
            <Box
              bgColor={"gray"}
              w={{ base: "100%", smx: "fit-content" }}
              maxW={{
                base: checker() ? "fit-content" : "100%",
                sm: checker() ? "fit-content" : "max-content",
              }}
              minW={{
                base: checker() ? "fit-content" : "full",
                sm: checker() ? "fit-content" : "100%",
              }}
              h={"3px"}
              borderRadius={"2px"}
              position={"absolute"}
              bottom={"1px"}
            ></Box>
            {/* <TabIndicator height="5px" borderRadius={'5px'} bg="red" /> */}
          </Flex>
        </TabList>
        {rightFade && (
          <Box
            w={"46px"}
            h={"24px"}
            position={"absolute"}
            top={"3px"}
            right={"0"}
            bgGradient={
              "linear-gradient(270deg, #F0F2F5 0%, rgba(240, 242, 245, 0.00) 77.17%)"
            }
            cursor={"pointer"}
            onClick={(e) => {
              e.stopPropagation();
              handleTabClick(tabsHeader.length - 1);
            }}
          ></Box>
        )}
      </MainWrapper>
      <TabPanels>
        {tabsPanels?.map((PanelComponent, index) => {
          return (
            <TabPanel p={"0px"} key={`companyTabPanel-${index}`}>
              <PanelComponent {...dataObject} />
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  );
};
